// DesktopUpdateToast Component
// Bottom-left pop-up showing "New version available" with version comparison
// Wired to backend UpdateManager via updaterAPI.checkForNewVersions (preload-exposed, available immediately)

import React, { useState, useEffect } from 'react';
import { RevoltButton } from './RevoltButton';
import { useAuth } from '../contexts/AuthContext';
import { envUtils } from '../core/utils';
import './DesktopUpdateToast.css';

export function DesktopUpdateToast() {
  const [isDismissed, setIsDismissed] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [updateState, setUpdateState] = useState('available'); // 'available' | 'downloading' | 'ready'
  const [downloadProgress, setDownloadProgress] = useState({ percent: 0 });
  const { isAuthenticated, isSupporter } = useAuth();

  // Check for new versions when desktop + auth + supporter (hooks must run before any early return)
  useEffect(() => {
    if (!envUtils.device.isDesktopApp() || !isAuthenticated || !isSupporter) return;

    const channel = envUtils.device.getDesktopChannel() || 'main';
    const api = window.updaterAPI || window.updateAPI;
    if (!api?.checkForNewVersions) return;

    api.checkForNewVersions(channel).then((result) => {
      setCheckResult(result);
    }).catch((err) => {
      setCheckResult({ updateAvailable: false, error: err?.message || 'Unknown error' });
    });
  }, [isAuthenticated, isSupporter]);

  // Fetch update info (canUpdate, isPackaged, isDev) when we might show the toast
  useEffect(() => {
    if (!envUtils.device.isDesktopApp() || !checkResult?.updateAvailable) return;
    const api = window.updaterAPI || window.updateAPI;
    const fn = api?.getUpdateInfo;
    if (typeof fn !== 'function') return;
    fn.call(api).then(setUpdateInfo).catch(() => setUpdateInfo({ canUpdate: false }));
  }, [checkResult?.updateAvailable]);

  // Subscribe to updater events when we can update
  useEffect(() => {
    const api = window.updaterAPI;
    if (!api || !updateInfo?.canUpdate) return;

    const unsubs = [
      api.onDownloadProgress?.((p) => {
        setUpdateState('downloading');
        setDownloadProgress(p || { percent: 0 });
      }),
      api.onUpdateDownloaded?.(() => {
        setUpdateState('ready');
      }),
      api.onError?.((err) => {
        setUpdateState('available');
        console.error('[DesktopUpdateToast] Update error:', err);
      }),
    ].filter(Boolean);

    return () => unsubs.forEach((fn) => typeof fn === 'function' && fn());
  }, [updateInfo?.canUpdate]);

  // Only in desktop app
  if (!envUtils.device.isDesktopApp()) {
    return null;
  }

  // Only when logged in and supporter (updates are for paying users)
  if (!isAuthenticated || !isSupporter) {
    return null;
  }

  // Only show when check completed and update is available
  if (!checkResult?.updateAvailable || !checkResult.newVersion) {
    return null;
  }

  if (isDismissed) {
    return null;
  }

  const currentVersion = checkResult.currentVersion || envUtils.device.getDesktopVersion() || '0.0.0';
  const newVersion = checkResult.newVersion;
  const canUpdate = updateInfo?.canUpdate ?? false;

  const handleLater = () => {
    setIsDismissed(true);
  };

  const handleUpdateNow = async () => {
    if (!canUpdate) {
      setIsDismissed(true);
      return;
    }
    const api = window.updaterAPI;
    if (!api?.downloadUpdate) return;
    const result = await api.downloadUpdate();
    if (!result?.success) {
      console.error('[DesktopUpdateToast] Download failed:', result?.error);
    }
  };

  const handleInstallNow = async () => {
    const api = window.updaterAPI;
    if (!api?.installUpdate) return;
    await api.installUpdate();
  };

  const titleSuffix = (updateInfo && !updateInfo.canUpdate) ? ' (dev)' : '';

  return (
    <div className="desktop-update-toast" role="alert">
      <div className="desktop-update-toast-content">
        <h3 className="desktop-update-toast-title">
          {updateState === 'downloading' && 'Downloading update'}
          {updateState === 'ready' && 'Update ready'}
          {updateState === 'available' && `New version available${titleSuffix}`}
        </h3>
        <div className="desktop-update-toast-versions">
          <span className="desktop-update-toast-current">{currentVersion}</span>
          <span className="desktop-update-toast-arrow">→</span>
          <span className="desktop-update-toast-new">{newVersion}</span>
        </div>
        {updateState === 'downloading' && (
          <div className="desktop-update-toast-progress">
            <div className="desktop-update-toast-progress-bar">
              <div className="desktop-update-toast-progress-fill" style={{ width: `${downloadProgress.percent || 0}%` }} />
            </div>
            <span className="desktop-update-toast-progress-text">{Math.round(downloadProgress.percent || 0)}%</span>
          </div>
        )}
        <div className="desktop-update-toast-actions">
          {updateState === 'ready' ? (
            <>
              <RevoltButton variant="ghost" onClick={handleLater}>Later</RevoltButton>
              <RevoltButton variant="accent" onClick={handleInstallNow}>Install Now</RevoltButton>
            </>
          ) : (
            <>
              <RevoltButton variant="ghost" onClick={handleLater}>Later</RevoltButton>
              <RevoltButton variant="accent" onClick={handleUpdateNow} disabled={updateState === 'downloading'}>
                {updateState === 'downloading' ? 'Downloading…' : 'Update Now'}
              </RevoltButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
